/**
 * Block 8.3 (2026-05-26): ФОП Reports API.
 */
import { api } from './client';


export type WarningLevel = 'OK' | 'INFO' | 'WARNING' | 'CRITICAL';


export interface SingleTaxSummary {
  organization_id: string;
  organization_name: string;
  tax_system: string;
  tax_group: number;
  year: number;
  total_tax_amount: string;
  documents_count: number;
  monthly_breakdown: Array<Record<string, unknown>> | null;
  quarterly_breakdown: Array<Record<string, unknown>> | null;
}

export interface FopSelfPaymentsSummary {
  organization_id: string;
  organization_name: string;
  year: number;
  total_esv_amount: string;
  esv_documents_count: number;
  total_wz_amount: string;
  wz_documents_count: number;
  monthly_breakdown: Array<{ month: number; esv: string; wz: string }>;
}

export interface AnnualLimitCheck {
  organization_id: string;
  organization_name: string;
  tax_system: string;
  tax_group: number;
  year: number;
  annual_limit: string;
  revenue_ytd: string;
  excluded_breakdown: Record<string, string>;
  usage_percent: string;
  remaining_amount: string;
  warning_level: WarningLevel;
  warning_message: string;
  recommended_action: string | null;
}

// Block 10 (2026-05-27): Bulk endpoint для dashboard widget.
export interface AnnualLimitBulkSummary {
  total_orgs: number;
  critical_count: number;
  warning_count: number;
  info_count: number;
  ok_count: number;
}

export interface AnnualLimitCheckBulkResponse {
  year: number;
  checks: AnnualLimitCheck[];
  summary: AnnualLimitBulkSummary;
}


const BASE = '/refs/fop-reports';


export const fopReports = {
  singleTaxSummary: (organization_id: string, year: number) =>
    api.get<SingleTaxSummary>(`${BASE}/single-tax-summary`, {
      params: { organization_id, year },
    }).then((r) => r.data),

  selfPaymentsSummary: (organization_id: string, year: number) =>
    api.get<FopSelfPaymentsSummary>(`${BASE}/self-payments-summary`, {
      params: { organization_id, year },
    }).then((r) => r.data),

  annualLimitCheck: (organization_id: string, year: number) =>
    api.get<AnnualLimitCheck>(`${BASE}/annual-limit-check`, {
      params: { organization_id, year },
    }).then((r) => r.data),

  annualLimitCheckBulk: (organization_ids: string[], year: number) =>
    api.get<AnnualLimitCheckBulkResponse>(`${BASE}/annual-limit-check-bulk`, {
      params: { organization_ids, year },
      paramsSerializer: { indexes: null },  // repeated `organization_ids=` керу
    }).then((r) => r.data),

  declarationGroup1_2Url: (organization_id: string, year: number) =>
    `/api/v1${BASE}/declaration-group-1-2/print?organization_id=${organization_id}&year=${year}`,

  declarationGroup3Url: (
    organization_id: string, year: number, quarter: number,
  ) =>
    `/api/v1${BASE}/declaration-group-3/print?organization_id=${organization_id}&year=${year}&quarter=${quarter}`,

  esvAnnualReportUrl: (organization_id: string, year: number) =>
    `/api/v1${BASE}/esv-annual-report/print?organization_id=${organization_id}&year=${year}`,
};
